import { Tenant } from '@velociti/core'
import { convert } from 'html-to-text'
import {
  ExtractDocumentTypeFromTypedRxJsonSchema,
  RxCollection,
  RxDocument,
  RxJsonSchema,
  toTypedRxJsonSchema,
} from 'rxdb'
import { Router } from 'vue-router'

export const messageSchemaLiteral = {
  title: 'Message Schema',
  description: 'Describes a message, which is an instance of a pack that has been sent to a recipient',
  version: 2,
  keyCompression: false,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 100,
    },
    uuid: {
      type: 'string',
    },
    pack: {
      ref: 'packs',
      type: 'string',
    },
    recipient: {
      ref: 'messageRecipients',
      type: 'string',
    },
    status: {
      type: 'string',
    },
    user: {
      ref: 'users',
      type: 'string',
    },
    sentAt: {
      type: 'string',
    },
    emailSubject: {
      type: 'string',
    },
    emailGreeting: {
      type: 'string',
    },
    emailValue: {
      type: ['string', 'null'],
    },
    emailLinkType: {
      /* Enum support here is not realistic... RxDB is not mature enough for us to do this */
      type: ['string', 'null'],
    },
    emailLinkText: {
      type: 'string',
    },
    value: {
      type: ['string', 'null'],
    },
    packVisits: {
      type: 'number',
    },
    downloads: {
      type: 'number',
    },
    firstVisit: {
      type: ['string', 'null'],
    },
    lastVisit: {
      type: ['string', 'null'],
    },
    signature: {
      ref: 'signatures',
      type: 'string',
    },
  },
  required: [
    'id',
    'uuid',
    'pack',
    'recipient',
    'status',
    'user',
    'sentAt',
    'emailSubject',
    'emailGreeting',
    'emailLinkType',
    'emailLinkText',
    'packVisits',
    'downloads',
    'signature',
  ],
  indexes: [],
} as const

export const messageMigrationStrategies = {
  1: (oldDoc: any) => ({
    ...oldDoc,
    subject: undefined,
    emailSubject: oldDoc.subject,
    emailGreeting: 'Hi,',
    emailValue: null,
    emailLinkType: 'button',
    emailLinkText: 'View Sales Pack',
  }),
  2: (oldDoc: any) => ({
    ...oldDoc,
    status: 'sent',
  }),
}

export const statusLabelMap = new Map([
  ['sent', 'Sent'],
  ['delivered', 'Delivered'],
  ['bounced', 'Bounced'],
  ['opened', 'Opened'],
  ['viewed', 'Viewed'],
  ['active', 'Active'],
  ['unsubscribed', 'Unsubscribed'],
])

export const statusIconMap = new Map([
  ['sent', '/icons/message/status/sent.svg'],
  ['delivered', '/icons/message/status/received.svg'],
  ['opened', '/icons/message/status/opened.svg'],
  ['active', '/icons/message/status/active.svg'],
  ['bounced', '/icons/message/status/bounced.svg'],
  ['unsubscribed', '/icons/message/status/unsubscribed.svg'],
  ['viewed', '/icons/message/status/viewed.svg'],
])

export const statusDescriptionMap = new Map([
  ['sent', 'The pack has been sent to the recipient, but not yet hit their inbox'],
  ['delivered', 'The pack has successfully been sent to the recipient'],
  ['opened', 'The recipient has viewed the email, but not yet opened the pack'],
  ['active', 'The recipient has viewed and interacted with the pack'],
  ['bounced', "The recipient's address was invalid"],
  ['unsubscribed', 'The recipient has opted out of receiving any packs'],
  ['viewed', 'The recipient has viewed the pack, but not yet interacted with it'],
])

const schemaTyped = toTypedRxJsonSchema(messageSchemaLiteral)

export type MessageDocType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof schemaTyped>

export const messageSchema: RxJsonSchema<MessageDocType> = schemaTyped

export type MessageDocMethods = {
  getEncodedPath: (tenant: Tenant) => string
  getMessageLink: (tenant: Tenant, router: Router) => string
  getMailToLink: (tenant: Tenant, router: Router) => Promise<string>
  getStatusLabel(): string | null
  getStatusIcon(): string | null
  getStatusDescription(): string | null
}

export const messageDocMethods = {
  getEncodedPath(tenant: Tenant): string {
    return btoa(JSON.stringify({ messageUuid: this.uuid, apiUrl: tenant.url }))
  },
  getMessageLink(tenant: Tenant, router: Router): string {
    const messagePath = router.resolve({
      name: 'message__view',
      params: { encodedPath: this.getEncodedPath(tenant) },
    }).href

    return window.location.origin + messagePath
  },
  async getMailToLink(tenant: Tenant, router: Router): Promise<string> {
    const recipient = await this.populate('recipient')

    // Added some last resort null checks here, but probably indicates something
    // needs fixing elsehwre.  This happened when Kev was loading pack performance stats
    // for a pack I had sent.
    const encodedName = encodeURIComponent(`${recipient?.firstName} ${recipient?.lastName}`)
    const encodedEmail = encodeURIComponent(recipient?.email)
    const encodedSubject = encodeURIComponent(this.emailSubject)

    let link = `mailto:${encodedEmail}?subject=${encodedSubject}`

    // copy sender
    if (false) {
      link += `&bcc=${false}`
    }

    link += `&body=Hi ${encodedName},%0A%0A`

    if (this.value) {
      const messageText = convert(this.value, { preserveNewlines: true })
      link += `${messageText}%0A%0A`
    }

    link += encodeURIComponent('Click here to view your pack:\n' + this.getMessageLink(tenant, router))

    return link
  },
  getStatusLabel(): string | null {
    return statusLabelMap.get(this.status) || null
  },
  getStatusIcon(): string | null {
    return statusIconMap.get(this.status) || null
  },
  getStatusDescription(): string | null {
    return statusDescriptionMap.get(this.status) || null
  },
}

export type MessageDocument = RxDocument<MessageDocType, MessageDocMethods>

export type MessageCollectionMethods = {
  test: () => void
}

export type MessageCollection = RxCollection<MessageDocType, MessageDocMethods, MessageCollectionMethods>
